import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { MoreHorizontal, Eye, Phone, Calendar, IndianRupee } from "lucide-react";

const BookingsPage = () => {
  const { API, getAuthHeader } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");

  useEffect(() => {
    fetchBookings();
  }, [filterStatus]);

  const fetchBookings = async () => {
    try {
      const params = new URLSearchParams();
      if (filterStatus !== "all") params.append("status", filterStatus);
      params.append("limit", "100");
      
      const response = await axios.get(`${API}/bookings?${params}`, getAuthHeader());
      setBookings(response.data.bookings);
      setTotal(response.data.total);
    } catch (error) {
      console.error("Fetch bookings error:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (bookingId, status) => {
    try {
      await axios.put(`${API}/bookings/${bookingId}/status?status=${status}`, {}, getAuthHeader());
      toast.success("Booking status updated");
      fetchBookings();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "confirmed":
        return "bg-emerald-100 text-emerald-700";
      case "completed":
        return "bg-blue-100 text-blue-700";
      case "cancelled":
        return "bg-red-100 text-red-700";
      case "cancel_requested":
        return "bg-red-50 text-red-600";
      case "modify_requested":
        return "bg-orange-100 text-orange-700";
      case "in_progress":
        return "bg-violet-100 text-violet-700";
      default:
        return "bg-amber-100 text-amber-700";
    }
  };

  const statuses = ["pending_payment", "confirmed", "in_progress", "completed", "cancelled", "cancel_requested", "modify_requested"];

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-950" data-testid="bookings-title">
            Bookings
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            {total} total bookings
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-zinc-200 shadow-none">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="space-y-1">
              <Label className="text-xs text-zinc-500">Status</Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-44" data-testid="filter-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {statuses.map((s) => (
                    <SelectItem key={s} value={s} className="capitalize">
                      {s.replace(/_/g, " ")}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bookings Table */}
      <Card className="border-zinc-200 shadow-none">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-zinc-50/50">
                <TableHead className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Booking ID</TableHead>
                <TableHead className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Customer</TableHead>
                <TableHead className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Travel</TableHead>
                <TableHead className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Services</TableHead>
                <TableHead className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Amount</TableHead>
                <TableHead className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Status</TableHead>
                <TableHead className="text-xs font-medium text-zinc-500 uppercase tracking-wider text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-zinc-500">
                    Loading bookings...
                  </TableCell>
                </TableRow>
              ) : bookings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-zinc-500">
                    No bookings found
                  </TableCell>
                </TableRow>
              ) : (
                bookings.map((booking) => (
                  <TableRow key={booking.id} className="hover:bg-zinc-50" data-testid={`booking-row-${booking.id}`}>
                    <TableCell>
                      <p className="font-mono text-sm text-zinc-900">{booking.booking_number || booking.id}</p>
                      <p className="text-xs text-zinc-500">{formatDate(booking.created_at)}</p>
                    </TableCell>
                    <TableCell>
                      <p className="text-sm font-medium text-zinc-900">{booking.customer_name || booking.customer?.name || "-"}</p>
                      {booking.customer_phone && (
                        <p className="text-xs text-zinc-500 flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          +{booking.customer_phone}
                        </p>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-3 h-3 text-zinc-400" />
                          <span className="text-sm">{booking.service_date || booking.travel_date || "-"}</span>
                        </div>
                        <p className="text-xs text-zinc-500 capitalize">
                          {[booking.travel_type, booking.travel_direction].filter(Boolean).join(" | ") || [booking.terminal, booking.zone].filter(Boolean).join(" | ") || "-"}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1 max-w-xs">
                        {booking.service_name ? (
                          <Badge variant="secondary" className="text-xs">
                            {booking.service_name}
                          </Badge>
                        ) : booking.services?.slice(0, 2).map((s, i) => (
                          <Badge key={i} variant="secondary" className="text-xs">
                            {s.name}
                          </Badge>
                        ))}
                        {booking.services?.length > 2 && (
                          <Badge variant="secondary" className="text-xs">
                            +{booking.services.length - 2}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="font-medium text-zinc-900">
                      {formatCurrency(booking.total_amount)}
                    </TableCell>
                    <TableCell>
                      <Badge className={`text-xs capitalize ${getStatusColor(booking.status)}`}>
                        {booking.status?.replace(/_/g, " ")}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" data-testid={`booking-actions-${booking.id}`}>
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {statuses.map((status) => (
                            <DropdownMenuItem
                              key={status}
                              onClick={() => updateStatus(booking.id, status)}
                              className="capitalize"
                            >
                              Mark as {status.replace(/_/g, " ")}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default BookingsPage;
